import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../../contexts/ThemeContext';

const PROD_DOMAIN = 'https://nopray.com';
const SUPPORTED_LANGS = ['en', 'ar', 'fr', 'es', 'ur'];

const RTL_LANGS = ['ar', 'ur'];

const OG_LOCALE_MAP = {
  en: 'en_US',
  ar: 'ar_SA',
  fr: 'fr_FR',
  es: 'es_ES',
  ur: 'ur_PK',
};

const DEFAULT_TITLES = {
  en: 'Never Miss A Prayer',
  ar: 'لا تفوت صلاة',
  fr: 'Never Miss A Prayer',
  es: 'Never Miss A Prayer',
  ur: 'نماز نہ چھوڑیں',
};

const DEFAULT_DESCRIPTIONS = {
  en: 'Accurate prayer times, Qibla direction, and daily prayer tracker. Muslim-built, privacy-focused, ad-free. Available in 5 languages.',
  ar: 'أوقات صلاة دقيقة، اتجاه القبلة، ومتابعة الصلوات اليومية. مبني من قِبَل مسلمين، يحترم الخصوصية، وخالٍ من الإعلانات.',
  fr: 'Horaires de prière précis, direction de la Qibla et suivi quotidien des prières. Construit par des musulmans, respectueux de la vie privée.',
  es: 'Horarios de oración precisos, dirección de la Qibla y seguimiento diario de oraciones. Construido por musulmanes, privacidad primero.',
  ur: 'درست نماز کے اوقات، قبلہ سمت، اور روزانہ نماز ٹریکر۔ مسلمانوں کا بنایا ہوا، پرائیویسی کا احترام۔',
};

const SEO = ({
  title,
  description,
  keywords,
  path = '/',
}) => {
  const { language, isRTL } = useTheme();

  const defaultTitle = DEFAULT_TITLES[language] || DEFAULT_TITLES.en;
  const defaultDescription = DEFAULT_DESCRIPTIONS[language] || DEFAULT_DESCRIPTIONS.en;
  const defaultKeywords = 'prayer times, salah, qibla, adhan, muslim, islam, namaz, prayer tracker, nopray, مواقيت الصلاة, horaires de prière, horarios de oración, نماز کے اوقات';

  const canonicalUrl = `${PROD_DOMAIN}${path}`;
  const fullTitle = title ? `${title} | ${defaultTitle}` : defaultTitle;
  const ogLocale = OG_LOCALE_MAP[language] || 'en_US';

  return (
    <Helmet>
      {/* HTML attributes */}
      <html lang={language} dir={isRTL ? 'rtl' : 'ltr'} />

      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content="Mohammed Radhi" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />

      {/* Canonical — always points to production domain */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang — multilingual alternates */}
      {SUPPORTED_LANGS.map((lang) => {
        const href = lang === 'en'
          ? `${PROD_DOMAIN}${path}`
          : `${PROD_DOMAIN}${path}${path.includes('?') ? '&' : '?'}lang=${lang}`;
        return <link key={lang} rel="alternate" hreflang={lang} href={href} />;
      })}
      <link rel="alternate" hreflang="x-default" href={`${PROD_DOMAIN}${path}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:locale" content={ogLocale} />
      {SUPPORTED_LANGS.filter(l => l !== language).map(l => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALE_MAP[l]} />
      ))}
      <meta property="og:site_name" content="Never Miss A Prayer — nopray.com" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@MrAlghafly" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />

      {/* Theme / PWA */}
      <meta name="theme-color" content="#1a365d" />
      <meta name="msapplication-navbutton-color" content="#1a365d" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="nopray" />
    </Helmet>
  );
};

export default SEO;
